import { TypeID, typeidUnboxed } from 'typeid-js';

/**
 * TypeID identifiers — decision N-2 in `docs/plans/nile-migration.md`.
 *
 * A row id is a per-table prefix plus a base32-encoded UUIDv7, e.g.
 * `student_01k2e5m4f3g7h9qrstvwxyz01`, stored as `text` so the value in the
 * database is byte-for-byte the value in the API.
 *
 * `ID_PREFIXES` below is the single source of truth for the prefixes. The
 * Postgres schema builds its primary keys and its `CHECK (id LIKE '<prefix>\_%')`
 * constraints from it, and route validation resolves prefixes through it, so a
 * prefix cannot drift between the two.
 *
 * Prefixes are lowercase letters only. The TypeID separator is `_`, so a prefix
 * containing one would be ambiguous to parse and would widen the `LIKE`
 * constraint; `ids.test.ts` enforces both properties.
 */
export const ID_PREFIXES = {
  schools: 'school',
  // A person at one school. Named `people` rather than `users` because most rows
  // here are records rather than accounts: `people.loginEnabled` defaults to
  // false, and an imported student who will never sign in is still a person the
  // school keeps. What they *are* at that school lives in `personRoles`.
  people: 'person',
  // Which offices a person holds at a school, and over what scope. A person may
  // hold several - a teacher who is also a parent, a coordinator who also
  // teaches - which is precisely what a single `people.role` column could not
  // say.
  personRoles: 'personrole',
  // The person behind one or more `people` rows. Deliberately un-tenanted: an
  // identity is what lets someone hold a membership at more than one school,
  // so it is the only id here that names something outside a school.
  identities: 'identity',
  academicYears: 'acadyear',
  departments: 'dept',
  teachers: 'teacher',
  students: 'student',
  courses: 'course',
  terms: 'term',
  termCourses: 'termcourse',
  categoryTemplates: 'cattemplate',
  assignmentCategories: 'category',
  assignments: 'assignment',
  enrollments: 'enrollment',
  grades: 'grade',
  gradeScales: 'gradescale',
  parentStudents: 'parentstudent',
  termSubmissions: 'submission',
  postedGrades: 'postedgrade',
  gradeChangeRequests: 'gradechange',
  gradeAuditLog: 'audit',
  studentHealth: 'health',
  studentDocuments: 'studentdoc',
  fieldPermissions: 'fieldperm',
  // One school's decision that an office does, or does not, hold a capability.
  // Absent rows mean the catalogue default, so this table also ships empty.
  rolePermissions: 'roleperm',
  studentCustomFields: 'customfield',
  homerooms: 'homeroom',
  attendanceRecords: 'attendance',
  // Only the people a guardian record does not already cover; see the table.
  authorizedPickups: 'pickup',
  reportCardTemplates: 'rctemplate',
  reportCardSections: 'rcsection',
  reportCardItems: 'rcitem',
  reportCards: 'reportcard',
  reportCardEntries: 'rcentry',
  reportCardAttendance: 'rcattendance',
  // One upload of enrolment forms, read in the background. The batch is the
  // unit a reviewer waits on and the unit whose stored PDFs are purged; the
  // documents under it are the unit of work, of cost, and of retry.
  intakeBatches: 'intakebatch',
  intakeDocuments: 'intakedoc',
} as const;

/** Schema export name of every table that has a TypeID primary key. */
export type IdTable = keyof typeof ID_PREFIXES;

/** Every prefix the schema uses, as a union. */
export type IdPrefix = (typeof ID_PREFIXES)[IdTable];

/** A TypeID for a specific table, e.g. `student_01k2e5m4f3g7h9qrstvwxyz01`. */
export type TypedId<P extends IdPrefix = IdPrefix> = `${P}_${string}`;

/** The prefixes, deduplicated into a list for iteration. */
export const ID_PREFIX_VALUES: readonly IdPrefix[] = Object.freeze(
  Object.values(ID_PREFIXES) as IdPrefix[]
);

const PREFIX_SET: ReadonlySet<string> = new Set<string>(ID_PREFIX_VALUES);

/** The prefix for a table, by its schema export name. */
export function prefixOf<T extends IdTable>(table: T): (typeof ID_PREFIXES)[T] {
  return ID_PREFIXES[table];
}

/**
 * A fresh identifier for `prefix`. Generated in the application rather than by
 * Postgres: it saves a round trip per insert from the Worker, and prefixing
 * requires app-side encoding anyway.
 */
export function newId<P extends IdPrefix>(prefix: P): TypedId<P> {
  return typeidUnboxed(prefix) as TypedId<P>;
}

/**
 * Splits an id into its prefix and 26-character suffix. Throws when `value` is
 * not a well-formed TypeID carrying a prefix this schema uses — use
 * {@link isValidId} where a boolean is wanted.
 */
export function parseId(value: string): { prefix: IdPrefix; suffix: string } {
  const separator = value.indexOf('_');
  if (separator <= 0) {
    throw new Error(`Invalid id, expected "<prefix>_<suffix>": ${value}`);
  }

  const prefix = value.slice(0, separator);
  if (!PREFIX_SET.has(prefix)) {
    throw new Error(`Unknown id prefix "${prefix}"`);
  }

  // Validates the suffix: 26 characters of the base32 alphabet, and a leading
  // character low enough that the decoded value fits 128 bits.
  const parsed = TypeID.fromString(value, prefix);

  return { prefix: prefix as IdPrefix, suffix: parsed.getSuffix() };
}

/**
 * Whether `value` is a well-formed TypeID carrying exactly `prefix`. This is
 * the check that keeps a course id out of a student id parameter, ahead of the
 * database's `CHECK` constraint.
 */
export function isValidId(prefix: IdPrefix, value: unknown): value is TypedId {
  if (typeof value !== 'string') return false;

  try {
    return parseId(value).prefix === prefix;
  } catch {
    return false;
  }
}
